import ResizeObserver from 'resize-observer-polyfill';
import ThisBound from 'lib/object/this-bound';
import {function_delay} from 'lib/utilities';

type Targets = HTMLElement | ArrayLike<HTMLElement> | string;
type Prop = 'width' | 'height' | 'top' | 'left';

const SIZE_PROP_MAP: {width: 'offsetWidth'; height: 'offsetHeight'} = {width: 'offsetWidth', height: 'offsetHeight'};

interface Options {
    target: Targets;
    source: HTMLElement | string;
    useRect?: boolean;
    rectProps?: Prop[] | Prop;
    relativeTo?: HTMLElement | string;
}

export default class MatchRect extends ThisBound {
    private readonly observer = new ResizeObserver(this.triggerResize);
    private readonly resizeDelayed = function_delay(250, this.resize);

    private readonly targets: ArrayLike<HTMLElement>;
    private readonly source: HTMLElement;
    private readonly relativeTo: HTMLElement;
    private readonly props: Prop[];
    private readonly options: Options;

    constructor(options: Options) {
        super(['resize', 'triggerResize']);

        const {target, source, rectProps = 'height', relativeTo = document.body} = options;
        this.options = options;
        this.targets = MatchRect.resolveTargets(target);
        this.source = typeof source === 'string' ? document.querySelector(source) : source;
        this.props = typeof rectProps === 'string' ? [rectProps] : rectProps;
        this.relativeTo = typeof relativeTo === 'string' ? document.querySelector(relativeTo) : relativeTo;

        this.resize();
        this.watchSource();
    }

    private static resolveTargets(targets: Targets): ArrayLike<HTMLElement> {
        if (typeof targets === 'string') {
            return document.querySelectorAll(targets);
        } else if (targets instanceof Node) {
            return [targets];
        } else {
            return targets;
        }
    }

    private destroy() {
        this.observer.disconnect();
        this.resizeDelayed.destroy();
    }

    private watchSource() {
        if (this.source) {
            this.observer.observe(this.source);
        } else {
            throw new Error('[MatchHeight]: Error - empty source specified');
        }
    }

    private triggerResize() {
        this.resize();
        this.resizeDelayed();
    }

    getRelativeRect(rect: ClientRect, relativeTo: ClientRect) {
        return {
            top: rect.top - relativeTo.top,
            left: rect.left - relativeTo.left,
            right: rect.right - relativeTo.right,
            bottom: rect.bottom - relativeTo.bottom,
            width: rect.width,
            height: rect.height
        }
    }

    resize() {
        const relativeToRect = this.relativeTo.getBoundingClientRect();
        const sourceRect = this.source.getBoundingClientRect();
        const rect = this.getRelativeRect(sourceRect, relativeToRect);

        const dimensions = this.props.map(name => {
            if (! this.options.useRect && (name in SIZE_PROP_MAP)) {
                return this.source[SIZE_PROP_MAP[name]];
            } else {
                return rect[name];
            }
        });

        for (let i = 0; i < this.targets.length; i++) {
            this.props.forEach((name, dIndex) => this.targets[i].style[name] = dimensions[dIndex] + 'px');
        }
    }
}